export const ACCOUNTING_TYPES = ['Documitra Fee', 'Government Fee'];

/** Record Receipt source (Step 01). Government Fee is the default. */
export const RECEIPT_SOURCES = ['From Government Fee', 'From Documitra Fee'] as const;

/** Documitra Fee receipt sections (Step 02). */
export const DOCUMITRA_RECEIPT_FEE_SECTIONS = ['Courier Label', 'Apostille Fee', 'Sales'] as const;

/** Leaf fee types stored on withdrawals / used in feeTotals */
export const FEE_TYPES = [
  'Government Fee',
  'Other Charges',
  'Courier Label',
  'Courier Fee',
  'Apostille',
  'Apostille Fee',
  'Documitra Fee',
  'Outside Payment',
  'Refund',
];

export const GOVERNMENT_FEE_TYPES = ['Government Fee', 'Other Charges'];

/** Member-wise Record Payment fee options (flat). */
export const RECORD_PAYMENT_FEE_TYPES = [
  'Government Fee',
  'Courier Label',
  'Other Charges',
  'Refund',
] as const;

export const DOCUMITRA_FEE_CATEGORIES = ['Courier Label', 'Apostille Fee', 'Sales'];

export const DOCUMITRA_RECEIPT_CATEGORIES = ['Additional Fees', 'Sales'] as const;

export const SALES_FEE_TYPES = ['Documitra Fee', 'Outside Payment'];

export const COURIER_PORTAL_OPTIONS = ['Pirate Ship', 'Shippo'];

/** Map order payment provider/method → voucher payment method. */
export const resolveOrderPaymentMethod = (order?: any): string => {
  const raw = String(
    order?.paymentDetails?.paymentMethod ||
      order?.paymentDetails?.provider ||
      order?.paymentMethod ||
      ''
  )
    .trim()
    .toLowerCase();
  if (!raw) return 'Stripe';
  if (raw.includes('zelle')) return 'Zelle';
  if (raw.includes('venmo')) return 'Venmo';
  if (raw.includes('stripe') || raw.includes('card') || raw.includes('credit')) return 'Stripe';
  if (
    raw.includes('bank') ||
    raw.includes('transfer') ||
    raw.includes('neft') ||
    raw.includes('imps') ||
    raw.includes('upi') ||
    raw.includes('inr')
  ) {
    return 'Bank Transfer';
  }
  return 'Stripe';
};

export const isCourierFeeType = (feeType?: string) =>
  feeType === 'Courier Label' || feeType === 'Courier Fee';

export const isApostilleFeeType = (feeType?: string) =>
  feeType === 'Apostille' || feeType === 'Apostille Fee';

/** Refund to the customer — captures refund id / amount / date / remark only. */
export const isRefundFeeType = (feeType?: string) => feeType === 'Refund';

export const VOUCHER_PAYMENT_METHODS = {
  Zelle: { currency: 'USD' },
  Venmo: { currency: 'USD' },
  Stripe: { currency: 'USD' },
  'Bank Transfer': {
    currency: 'INR',
    accounts: ['Documitra-ICICI', 'Documitra-IDFC', 'US-India Tax Corp-HDFC'],
  },
};

/** "Received In" for refund payment reconciliation: wallets + receiving bank accounts. */
export const REFUND_RECEIVED_IN_OPTIONS: string[] = [
  ...Object.keys(VOUCHER_PAYMENT_METHODS).filter(method => method !== 'Bank Transfer'),
  ...VOUCHER_PAYMENT_METHODS['Bank Transfer'].accounts,
];

export const WITHDRAW_PAYMENT_MODES = {
  'Credit/Debit Card': [
    'Priyanka-9132',
    'Priyanka-0497',
    'Anvit-3796',
    'Jai-1934',
    'Chase-2991 (Debit)',
    'Other',
  ],
  'Bank Transfer': ['Documitra-ICICI', 'Documitra-IDFC', 'US-India Tax Corp-HDFC'],
  PayPal: null,
  'Priyanka Personal': null,
};

export const VOUCHER_STATUS_COLORS: { [key: string]: string } = {
  Generated: 'blue',
  'Partially Paid': 'orange',
  Settled: 'green',
  Cancelled: 'red',
};

/**
 * Accounting workflow status. Updatable only from Transactions → Payment
 * Vouchers, by Accountant / Super Admin.
 */
export const ACCOUNTING_STATUSES = ['Pending', 'Recorded', 'Pending Settlement'] as const;

export const DEFAULT_ACCOUNTING_STATUS = 'Pending';

/** Status pill palette (static / read-only). Editable mode uses CMS Select chrome. */
export const ACCOUNTING_STATUS_STYLES: {
  [key: string]: { background: string; border: string; text: string; dot: string };
} = {
  Pending: { background: '#e2e8f0', border: '#94a3b8', text: '#0f172a', dot: '#64748b' },
  Recorded: { background: '#dcfce7', border: '#4ade80', text: '#166534', dot: '#16a34a' },
  'Pending Settlement': {
    background: '#ffedd5',
    border: '#fb923c',
    text: '#9a3412',
    dot: '#ea580c',
  },
};

export const toAmount = (value: any) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatMoney = (value: any, currency = 'USD') => {
  const amount = toAmount(value);
  const symbol = currency === 'INR' ? '₹' : '$';
  return `${symbol}${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
